import { useCallback, useEffect, useRef, useState } from 'react';

export function useEmergencyAlarm(isActive: boolean) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const sirenTimerRef = useRef<number | null>(null);
  const vibrationTimerRef = useRef<number | null>(null);
  const [audioStatus, setAudioStatus] = useState<'idle' | 'playing' | 'blocked' | 'unsupported'>('idle');

  const stopAlarm = useCallback(() => {
    if (sirenTimerRef.current) {
      window.clearInterval(sirenTimerRef.current);
      sirenTimerRef.current = null;
    }

    if (vibrationTimerRef.current) {
      window.clearInterval(vibrationTimerRef.current);
      vibrationTimerRef.current = null;
    }

    navigator.vibrate?.(0);
    oscillatorRef.current?.stop();
    oscillatorRef.current?.disconnect();
    gainRef.current?.disconnect();
    oscillatorRef.current = null;
    gainRef.current = null;
    setAudioStatus('idle');
  }, []);

  const startAlarm = useCallback(async () => {
    if (!isActive) {
      return;
    }

    if ('vibrate' in navigator) {
      navigator.vibrate([700, 120, 700, 120, 1200]);
      if (!vibrationTimerRef.current) {
        vibrationTimerRef.current = window.setInterval(() => {
          navigator.vibrate?.([700, 120, 700, 120, 1200]);
        }, 3200);
      }
    }

    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextConstructor) {
      setAudioStatus('unsupported');
      return;
    }

    try {
      const audioContext = audioContextRef.current ?? new AudioContextConstructor();
      audioContextRef.current = audioContext;
      await audioContext.resume();

      if (oscillatorRef.current) {
        setAudioStatus('playing');
        return;
      }

      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = 'sawtooth';
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.22, audioContext.currentTime + 0.08);
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      oscillatorRef.current = oscillator;
      gainRef.current = gain;

      let high = false;
      const sweep = () => {
        const now = audioContext.currentTime;
        oscillator.frequency.cancelScheduledValues(now);
        oscillator.frequency.setValueAtTime(high ? 980 : 520, now);
        oscillator.frequency.linearRampToValueAtTime(high ? 520 : 980, now + 0.45);
        high = !high;
      };

      sweep();
      sirenTimerRef.current = window.setInterval(sweep, 460);
      setAudioStatus('playing');
    } catch {
      setAudioStatus('blocked');
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive) {
      stopAlarm();
      return;
    }

    startAlarm();
    return stopAlarm;
  }, [isActive, startAlarm, stopAlarm]);

  return {
    audioStatus,
    startAlarm,
    stopAlarm,
  };
}
